import React, { FC, useState } from 'react'

import { useCombobox } from 'downshift'
import { Modifier, usePopper } from 'react-popper'
import maxSize from 'popper-max-size-modifier'
import Caret from '@mortgage-pos/ui/assets/Caret.svg'
import { GoogleAutoCompleteResult } from '@mortgage-pos/types'
import { UseAsyncReturn } from 'react-async-hook'
import { setAllFieldsToEmpty } from './helpers'
import { ThunkCreator } from 'easy-peasy'
import { ElementTrackingPayload } from '@mortgage-pos/ui/store/tracking'
import { ErrorIcon } from '@mortgage-pos/ui/components/ErrorIcon'

interface Props {
  name: string
  value: any
  setFieldValue: (arg0: string, arg1: string) => void
  onChange: (e: any) => void
  results: UseAsyncReturn<GoogleAutoCompleteResult, string[]>
  inputText: string
  setInputText: (string) => void
  addressFields: string[]
  error?: string
  touched?: boolean
  setEnterAddressManually: (boolean) => void
  elementClicked: ThunkCreator<ElementTrackingPayload, any>
}

const NO_OPTS_DESCRIPTION = "Let's fix it."

export const ComboBox: FC<Props> = ({
  name,
  onChange,
  value,
  setFieldValue,
  inputText,
  addressFields,
  results,
  error,
  touched,
  setInputText,
  setEnterAddressManually,
  elementClicked,
}) => {
  const errorClass = error && touched ? '--error' : ''
  const [hover, setHover] = useState(false)
  const [anchorElement, setAnchorElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const predictions =
    results?.result?.predictions.length > 0
      ? results?.result.predictions
      : [
          {
            description: NO_OPTS_DESCRIPTION,
          },
        ]

  const { styles, attributes, update } = usePopper(
    anchorElement,
    popperElement,
    {
      modifiers,
    }
  )

  /**
   * useComboBox (https://www.downshift-js.com/use-combobox) hook for WAI-ARIA compliant dropdowns
   */
  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    toggleMenu,
  } = useCombobox({
    items: predictions,
    itemToString: (item) => (item ? item.description : ''),
    selectedItem: value,
    initialInputValue: value ?? '',
    onSelectedItemChange: ({ inputValue }) => {
      if (inputValue === NO_OPTS_DESCRIPTION) {
        setAllFieldsToEmpty(name, setFieldValue, addressFields)
        setEnterAddressManually(true)
        elementClicked({
          elementType: 'Button',
          location: 'Autocomplete field',
          actionOutcome: 'Shows other address fields',
          text: "Can't find your address?",
        })
        return
      }
      onChange({
        target: {
          name,
          value: inputValue,
        },
      })
    },
    onIsOpenChange: () => {
      update && update()
    },
    onInputValueChange: ({ inputValue }) => {
      onChange({
        target: {
          name,
          value: inputValue,
        },
      })
    },
  })

  return (
    <div className={`input address-fields-auto-complete__input ${errorClass}`}>
      <label className="input__subtitle" htmlFor={name} {...getLabelProps()}>
        Address
      </label>
      <div {...getComboboxProps()} className="select__input-wrapper">
        <input
          placeholder="Enter a location"
          className="input__field"
          name={name}
          data-testid={name}
          {...getInputProps({ ref: setAnchorElement, autoComplete: 'off' })}
        />
        {inputText.length > 0 ? (
          <div
            className="address-fields-auto-complete__close-icon"
            onClick={() => {
              setInputText('')
              setAllFieldsToEmpty(name, setFieldValue, addressFields)
            }}
            data-testid="auto-complete-clear-input-button"
          />
        ) : null}
        <button
          className={`select__toggle-button`}
          type="button"
          aria-label="toggle menu"
          tabIndex={-1}
          onClick={toggleMenu}
          data-testid="auto-complete-toggle-menu-button"
        >
          <img
            src={Caret}
            alt="Select Caret"
            className={`select__caret ${isOpen ? '--active' : ''}`}
            aria-label="toggle menu"
          />
        </button>
      </div>
      <ul
        className={`select__popper ${isOpen ? '--open' : ''} ${
          (predictions?.length === 0 && !results.loading) ||
          (predictions.length === 1 &&
            !results.loading &&
            predictions[0].description === NO_OPTS_DESCRIPTION)
            ? '--error'
            : ''
        }`}
        {...getMenuProps({ ref: setPopperElement })}
        {...attributes.popper}
        style={styles.popper}
        data-testid={`auto-complete-address-fields-options-${name}`}
      >
        {isOpen && !results.loading ? (
          predictions?.map((item, index) => {
            if (item.description === NO_OPTS_DESCRIPTION) {
              return (
                <li
                  key={item.description}
                  data-testid="auto-complete-address-no-options"
                  className={`select__option ${
                    highlightedIndex === index ? '--error-active' : '--error'
                  }`}
                  onMouseOver={() => setHover(true)}
                  onMouseOut={() => setHover(false)}
                  onFocus={() => setHover(true)}
                  onBlur={() => setHover(false)}
                  onTouchEnd={() => setHover(false)}
                  onTouchStart={() => setHover(true)}
                  {...getItemProps({ item, index })}
                  onClick={() => {
                    setAllFieldsToEmpty(name, setFieldValue, addressFields)
                    setEnterAddressManually(true)
                    elementClicked({
                      elementType: 'Button',
                      location: 'Autocomplete field',
                      actionOutcome: 'Shows other address fields',
                      text: "Can't find your address?",
                    })
                  }}
                >
                  <span>
                    Uh-oh! We can't find your address.{' '}
                    <button
                      type="button"
                      className="address-fields-auto-complete__cant-find-address-button"
                      data-testid="auto-complete-cant-find-address"
                    >
                      Let's fix it.
                    </button>
                  </span>
                  <ErrorIcon hover={hover} />
                </li>
              )
            }
            return (
              <li
                key={item.description}
                data-testid="auto-complete-address-option"
                className={`select__option ${
                  highlightedIndex === index ? '--active' : ''
                }`}
                {...getItemProps({ item, index })}
              >
                {item.description}
              </li>
            )
          })
        ) : (
          <li
            key={'no-options'}
            data-testid="auto-complete-address-no-options"
            className={`select__option`}
            {...getItemProps({ item: null, index: 0 })}
          >
            Loading Results
          </li>
        )}
      </ul>
    </div>
  )
}

const modifiers: Modifier<string, object>[] = [
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['top', 'right'],
    },
  },
  {
    name: 'offset',
    options: {
      offset: [0, 10],
    },
  },
  // custom modifier to set the popper element to the same width as the anchor element
  {
    name: 'sameWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      state.styles.popper.width = `${state.rects.reference.width}px`
    },
    // @ts-ignore
    effect: ({ state }) => {
      // @ts-ignore. Popper jst typings are incomplete for html elements
      state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`
    },
  },
  maxSize,
  {
    name: 'applyMaxSize',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['maxSize'],
    fn({ state }) {
      const { height } = state.modifiersData.maxSize
      state.styles.popper.maxHeight = `${height / 1.2}px`
    },
  },
]
